const defaultParams = {
  extent: {
    width: 1,
    height: 1
  },
  // Mesh density, and so both how fine the map is drawn and how many steps the journey takes: mean
  // step length is about 1/sqrt(2*npts). Was 16384, which now runs the road to ~350 steps; 12288
  // puts it back at ~280, where it has always been, and costs proportionally less to generate.
  // Tune it against `npm run mapstat -- --runs=10 --assert`.
  npts: 12288,
  ncities: 15,
  nterrs: 5,
  // The shape of the country, and how much of it is sea.
  //
  // generateCoast draws a proto-landscape by adding three primitives, erodes it, and then cuts sea
  // level at a *quantile* of the resulting heights — so `sea` is literally the fraction of the
  // world that ends up underwater. These were hardcoded, and they made a map the wrong shape for
  // the game: a median world was 40% ocean and a third of the walk (p90: half of it) ran within
  // 0.04 of open water. This is an overland journey west. The map should read as a country with
  // water in it, not a coastline with land behind it.
  //
  // Tune against `npm run mapstat -- --runs=10 --assert`, which reports `land %` and
  // `route coastal %` and fails on either leaving its band.
  terrain: {
    // Tectonic uplift across the map, as the scale of a normal-distributed vector — the term that
    // gives water a direction to drain.
    //
    // **A decisive tilt is what keeps the sea out of the way, and this was measured the wrong way
    // round first.** The obvious read is that the tilt caused the problem: at 4 it dwarfed the other
    // two primitives, the world was a ramp, and sea level cut a straight coast across the middle of
    // it. But it was the sea level doing that, and flattening the tilt to 1.2 made the walk *more*
    // coastal, not less — a nearly level country drains into scattered shallow basins, and the road
    // wanders past their shores. A tilt of 2 pours the same amount of water into one corner and
    // leaves the rest of the country dry: over 20 worlds it halved the coastal tail (p90 24% -> 14%)
    // against no change in how much sea there is. Do not flatten this further.
    tilt: 2,
    // A radial cone, negative so the middle stands up and the margins fall away, which is what holds
    // the remaining water out at the edges rather than in the country the player walks through.
    // Measured, it is the weakest of the three — doubling it moved nothing outside the noise — so it
    // is left near where it was. Drawn as a range, like `sea` below.
    cone: [-1.5, -1.1],
    // Rounded blobs, the relief erosion then cuts valleys into. More of them than the 50 this used
    // to be: with the ocean gone the interior has to carry the map on its own, and the drawn relief
    // roughly doubled (~2500 slope strokes to ~5800), which is what stops an 88%-land world reading
    // as a blank page with a road on it.
    mountains: 70,
    // The fraction of the world below sea level, drawn per world. **This is the knob**, and it is
    // near enough exact: sea level is cut at this quantile of the height field, so a world comes out
    // about `1 - sea` land. It was [0.2, 0.6] — a median world 40% ocean.
    sea: [0.08, 0.16]
  },
  fontsizes: {
    region: 40,
    city: 25,
    town: 20
  }
};

const getEnvVariable = (key, defaultValue) => {
  return process.env[key] !== undefined ? process.env[key] : defaultValue;
};

// World generation. Read fresh at each call, like Clock() — the map is generated once at mount, but
// scripts/mapstat.js changes the seed between runs in the same process.
const Map = () => {
  const sea = getEnvVariable('REACT_APP_MAP_SEA', null);
  const seaOverride = sea === null || isNaN(parseFloat(sea)) ? null : parseFloat(sea);

  return {
    ...defaultParams,
    // Unset means Math.random: a new world every load, which is the game's normal state. Set it to
    // pin the world — the same seed yields the same coastline, road, towns and terrain sequence.
    seed: getEnvVariable('REACT_APP_MAP_SEED', null),
    // Mesh density. This is the journey-length knob as much as the detail knob: mean step length is
    // about 1/sqrt(2*npts), so halving npts makes each step longer and the road proportionally
    // shorter in steps — and generating the coast is two thirds of the mount-time cost.
    npts: parseInt(getEnvVariable('REACT_APP_MAP_NPTS', defaultParams.npts), 10),
    terrain: {
      ...defaultParams.terrain,
      // The one terrain knob worth reaching for without editing the file: the fraction of the world
      // under water, as a single number pinning the range above. `REACT_APP_MAP_SEA=0.4` gets the
      // old ocean-heavy worlds back.
      ...(seaOverride === null ? {} : { sea: [seaOverride, seaOverride] })
    }
  };
};

// The game clock. A phase is half a day, so the day/night icon flips every phaseMs.
const Clock = () => ({
  dayMs: parseInt(getEnvVariable('REACT_APP_DAY_MS', 60000), 10),
  tickMs: parseInt(getEnvVariable('REACT_APP_TICK_MS', 250), 10),
  // How much elapsed time may bank up while the clock is frozen. One phase means a
  // backgrounded tab can never hand the player several days at once.
  maxCatchUpPhases: parseInt(getEnvVariable('REACT_APP_MAX_CATCHUP_PHASES', 1), 10),
  enabled: getEnvVariable('REACT_APP_CLOCK_ENABLED', 'true') !== 'false',
  exposeForTests: getEnvVariable('REACT_APP_EXPOSE_CLOCK', 'false') === 'true',
  // The debug readout. On in development, off in a production build. Set REACT_APP_SHOW_CLOCK
  // explicitly to override either way — .env.test turns it off so its text can't collide with
  // the e2e locators.
  showDebug:
    getEnvVariable('REACT_APP_SHOW_CLOCK', process.env.NODE_ENV === 'development' ? 'true' : 'false') !== 'false',
  // How long the player has to reach the first town before the road kills them.
  deadlineDays: parseFloat(getEnvVariable('REACT_APP_WALK_DEADLINE_DAYS', 1)),
  // Ambient prose is one line per phase, placed at a jittered point inside it — the rate is
  // the phase, not a wall-clock interval, so it cannot outrun the world it describes.
  // Set REACT_APP_AMBIENT=false to silence it.
  ambientEnabled: getEnvVariable('REACT_APP_AMBIENT', 'true') !== 'false',
  get phaseMs() {
    return Math.round(this.dayMs / 2);
  },
  get phasesPerDay() {
    return 2;
  },
  get deadlinePhases() {
    return Math.max(1, Math.round(this.deadlineDays * this.phasesPerDay));
  }
});

export { Clock, Map };
export default defaultParams;
